import Offer from "@/assets/images/task/offer.png";
import RolesAvailable from "@/assets/images/task/roles.png";
import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
import BgSvg from "@/assets/svg/task/cta.svg";
import YoutubeSVG from "@/assets/svg/youtube-icon.svg";
import CaseStudy from "@/assets/videos/task/case-study.mp4";
import MinimumCost from "@/assets/videos/task/minimum-cost.mp4";
import TopTalents from "@/assets/videos/task/top-talents.mp4";
import Button from "@/components/ui/button";
import scrollTo from "@/utils/scrollTo";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function TaskHeader() {
  useEffect(() => {
    const videos = document.querySelectorAll("video[data-src]");

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const video = entry.target;
            video.src = video.dataset.src;
            video.load();
            video.play().catch(() => { });
            video.addEventListener("loadeddata", () => {
              video.classList.add("image-transition-loaded");
            });
            obs.unobserve(video);
          }
        });
      },
      {
        rootMargin: "200px",
        threshold: 0.25,
      }
    );

    videos.forEach((v) => observer.observe(v));
  }, []);

  return (
    <div className="relative overflow-hidden">
      <img
        src={BgSvg}
        className="absolute top-1/2 -translate-y-1/2 z-0 w-full pointer-events-none opacity-5"
        alt="Image"
      />
      <div className="p-6 md:p-16 pt-24 md:pt-28 lg:pt-24 4xl:pt-48 flex flex-col lg:flex-row justify-between items-center gap-12 lg:gap-16 lg:h-screen 4xl:h-[700px] relative overflow-hidden 4xl:max-w-[100rem] 4xl:mx-auto">
        <div className="lg:w-1/2 animate-left-entry space-y-6 lg:space-y-5 xl:space-y-6">
          <a
            href="https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/"
            target="_blank"
          >
            <div
              className="flex items-center gap-2 w-fit rounded-lg py-2 px-4 
        border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge"
            >
              <img src={YoutubeSVG} className="h-8 w-8" alt="YouTube Icon" />
              Official YouTube Certified
              <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
            </div>
          </a>
          <h1 className="text-4xl xs:text-5xl md:text-5xl lg:text-3xl xl:text-5xl 1xl:text-6xl font-medium text-left text-white">
            You <span className="text-primary">create</span>
            <br className="md:hidden" /> we handle
            <br />
            <span className="text-primary">everything else</span>
          </h1>
          <p className="text-white text-lg leading-6 transition-all duration-700 ease-out text-left lg:max-w-[23rem]">
            EnfiniTask is your A–Z team of YouTube-Certified Pros, taking care
            of everything AI can’t — so you can stay focused on creating.
          </p>
          <div className="flex items-center gap-4 pt-10 justify-start relative">
            <img
              src={Offer}
              className="absolute right-8 xs:right-10 sm:right-16 md:right-[280px] lg:right-0 xl:right-32 1xl:right-[240px] 2xl:right-[250px] -top-1 max-w-32 md:max-w-40"
              alt="Image"
            />
            <Button
              className="group relative rounded-full shadow bg-white text-primary py-6 w-44 flex items-center justify-center gap-2 overflow-hidden hover:text-white hover:bg-primary"
              onClick={() => scrollTo("explore-roles")}
            >
              <span className="transform transition-transform duration-300 group-hover:translate-x-5">
                Learn more
              </span>
              <ArrowDown className="transform transition-transform duration-300 group-hover:-translate-x-[85px]" />
            </Button>
            <Link to="/hire-a-talent">
              <Button className="group relative rounded-full shadow bg-primary text-white py-6 w-44 flex items-center justify-center gap-2 overflow-hidden hover:text-primary hover:bg-white">
                <span className="transform transition-transform duration-300 group-hover:translate-x-5">
                  Build your team
                </span>
                <ArrowRight className="transform transition-transform duration-300 group-hover:-translate-x-[110px]" />
              </Button>
            </Link>
          </div>
          <img
            src={RolesAvailable}
            alt="Roles"
            className="max-w-40 "
            onLoad={(e) =>
              e.currentTarget.classList.add("image-transition-loaded")
            }
          />
        </div>

        <div className="lg:w-[65%] xl:w-[60%] animate-right-entry hidden lg:flex lg:flex-col justify-start gap-4 h-[450px] xl:h-[640px] 1xl:h-[650px] 2xl:h-[620px] my-auto relative overflow-hidden py-2">
          <div className="flex items-end justify-end gap-4 -mr-10">
            <video
              data-src={CaseStudy}
              preload="metadata"
              muted
              playsInline
              autoPlay
              controls={false}
              loop={false}
              onLoadedData={(e) => {
                const el = e.currentTarget;
                el.classList.add("image-transition-loaded");
                el.addEventListener(
                  "transitionend",
                  () => el.classList.add("image-idle-float"),
                  { once: true }
                );
              }}
              className="rounded-[13px] lg:max-w-44 xl:max-w-60 1xl:max-w-72 2xl:max-w-52 image-transition image-float-hover"
            />
            <video
              data-src={MinimumCost}
              preload="metadata"
              muted
              playsInline
              autoPlay
              controls={false}
              loop={false}
              onLoadedData={(e) => {
                const el = e.currentTarget;
                el.classList.add("image-transition-loaded");
                el.addEventListener(
                  "transitionend",
                  () => el.classList.add("image-idle-float"),
                  { once: true }
                );
              }}
              className="rounded-[13px] lg:max-w-72 xl:max-w-[380px] 1xl:max-w-[430px] 2xl:max-w-[390px] image-transition image-float-hover"
            />
          </div>

          <video
            data-src={TopTalents}
            preload="metadata"
            muted
            playsInline
            autoPlay
            controls={false}
            loop={false}
            onLoadedData={(e) => {
              const el = e.currentTarget;
              el.classList.add("image-transition-loaded");
              el.addEventListener(
                "transitionend",
                () => el.classList.add("image-idle-float"),
                { once: true }
              );
            }}
            className="rounded-[13px] lg:max-w-[550px] xl:max-w-[780px] 1xl:max-w-[850px] 2xl:max-w-[780px] absolute lg:-bottom-12 xl:-bottom-10 1xl:-bottom-24 2xl:-bottom-16 4xl:-right-4 image-transition image-float-hover border border-[#434242] ml-auto"
          />
        </div>

        <div className=" w-full lg:w-1/2 animate-right-entry lg:hidden space-y-4 py-2">
          <div className="flex items-end justify-between md:justify-end gap-4">
            <img
              src={CaseStudy}
              className="rounded-[13px] max-w-40 md:max-w-60 image-transition image-float-hover"
              onLoad={(e) => {
                const el = e.currentTarget;
                el.classList.add("image-transition-loaded");
                el.addEventListener(
                  "transitionend",
                  () => {
                    el.classList.add("image-idle-float");
                  },
                  { once: true }
                );
              }}
              alt="Case Study"
            />
            <img
              src={MinimumCost}
              className="rounded-[13px] max-w-72 md:max-w-96 md:-mr-24 image-transition image-float-hover"
              onLoad={(e) => {
                const el = e.currentTarget;
                el.classList.add("image-transition-loaded");
                el.addEventListener(
                  "transitionend",
                  () => {
                    el.classList.add("image-idle-float");
                  },
                  { once: true }
                );
              }}
              alt="Mostly Minimum Cost"
            />
          </div>
          <img
            src={TopTalents}
            className="rounded-[13px] max-w-[700px] image-transition image-float-hover border border-[#434242]"
            onLoad={(e) => {
              const el = e.currentTarget;
              el.classList.add("image-transition-loaded");
              el.addEventListener(
                "transitionend",
                () => {
                  el.classList.add("image-idle-float");
                },
                { once: true }
              );
            }}
            alt="Top Talents"
          />
        </div>
      </div>
    </div>
  );
}
